if (!customElements.get('bundle-recomendation')) {
  class BundleRecomendation extends HTMLElement {
    constructor() {
      super();
      this.errorTimer = null;
      this.bundleProducts = new Map();
      this.leftSelected = null;
      this.rightSelected = null;
    }

    connectedCallback() {
      this.shopDomain = this.dataset.shopDomain;
      this.storefrontToken = this.dataset.storefrontToken;
      this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
      this.currencyCode = this.dataset.currencyCode || 'EUR';
      this.leftDefaultPrice = parseInt(this.dataset.leftDefaultPrice, 10) || 0;
      this.rightDefaultPrice = 0;

      this.leftCard = this.querySelector('[data-side="left"]');
      this.rightCard = this.querySelector('[data-side="right"]');
      this.leftVariantSelect = this.leftCard.querySelector('[data-bundle-variant-select]');
      this.rightProductSelect = this.rightCard.querySelector('[data-bundle-product-select]');
      this.rightVariantSelect = this.rightCard.querySelector('[data-bundle-variant-select]');
      this.rightImg = this.rightCard.querySelector('[data-bundle-img]');
      this.rightPrice = this.rightCard.querySelector('[data-bundle-price]');
      this.rightOptionName = this.rightCard.querySelector('[data-bundle-option-name]');
      this.totalsRoot = this.querySelector('[data-bundle-totals]');
      this.messageEl = this.querySelector('[data-bundle-message]');
      this.priceBlock = this.querySelector('[data-bundle-price-block]');
      this.totalOriginal = this.querySelector('[data-bundle-total-original]');
      this.totalFinal = this.querySelector('[data-bundle-total-final]');
      this.totalSaved = this.querySelector('[data-bundle-total-saved]');
      this.addBtn = this.querySelector('[data-bundle-add]');
      this.errorEl = this.querySelector('[data-bundle-error]');

      this.pickerRoot = this.rightCard.querySelector('[data-bundle-product-picker]');
      this.pickerTrigger = this.rightCard.querySelector('[data-bundle-picker-trigger]');
      this.pickerLabel = this.rightCard.querySelector('[data-bundle-picker-label]');
      this.pickerList = this.rightCard.querySelector('[data-bundle-picker-list]');
      this.pickerModal = this.rightCard.querySelector('[data-bundle-picker-modal]');
      this.pickerBackdrop = this.rightCard.querySelector('[data-bundle-picker-backdrop]');
      this.pickerClose = this.rightCard.querySelector('[data-bundle-picker-close]');

      this.bindEvents();
      this.bindPicker();
      this.loadBundleProducts();
      this.updateTotals();
    }

    bindEvents() {
      this.leftVariantSelect.addEventListener('change', () => {
        const opt = this.leftVariantSelect.selectedOptions[0];
        if (!opt || !opt.value) {
          this.leftSelected = null;
        } else {
          this.leftSelected = {
            id: opt.value,
            price: parseInt(opt.dataset.price, 10),
            compareAtPrice: parseInt(opt.dataset.compareAtPrice, 10) || 0,
            available: opt.dataset.available === 'true',
            optionValue: opt.dataset.optionValue,
          };
        }
        this.updateLeftOptionLabel();
        this.updateTotals();
        this.toggleActiveCard();
      });

      this.rightProductSelect.addEventListener('change', () => {
        this.renderRightProduct(this.rightProductSelect.value);
      });

      this.rightVariantSelect.addEventListener('change', () => {
        const opt = this.rightVariantSelect.selectedOptions[0];
        if (!opt || !opt.value) {
          this.rightSelected = null;
        } else {
          this.rightSelected = {
            id: opt.dataset.numericId,
            price: parseFloat(opt.dataset.price) * 100,
            compareAtPrice: parseFloat(opt.dataset.compareAtPrice || 0) * 100,
            available: opt.dataset.available === 'true',
            optionValue: opt.dataset.optionValue,
          };
        }
        this.updateRightOptionLabel();
        this.updateTotals();
        this.toggleActiveCard();
      });

      this.addBtn.addEventListener('click', () => this.onAddToCart());
    }

    bindPicker() {
      if (!this.pickerRoot || !this.pickerTrigger || !this.pickerList || !this.pickerModal) return;

      // Portal the modal to <body>. Any ancestor that uses `transform`,
      // `filter`, `perspective`, `will-change` or `contain` becomes the
      // containing block for `position: fixed`, which would force the modal
      // to render relative to that ancestor instead of the viewport (the bug
      // we saw where the modal floated above the page near the header). The
      // PDP info column is sticky/transformed in this theme, so we move the
      // modal node out of that subtree once at init time.
      if (this.pickerModal.parentElement !== document.body) {
        document.body.appendChild(this.pickerModal);
      }

      this.pickerTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openPicker();
      });

      this.pickerList.querySelectorAll('.bundle-product-picker__item').forEach((item) => {
        item.addEventListener('click', () => {
          const handle = item.dataset.handle;
          const title = item.dataset.title;
          this.selectPickerItem(handle, title);
        });
      });

      // Close when clicking outside the dialog. We listen on the modal wrapper
      // itself so both the backdrop layer and any padding around the dialog
      // dismiss the picker. The dialog stops propagation via its own children.
      this.pickerModal.addEventListener('click', (e) => {
        if (e.target === this.pickerModal || e.target === this.pickerBackdrop) {
          this.closePicker();
        }
      });
      if (this.pickerClose) {
        this.pickerClose.addEventListener('click', () => this.closePicker());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.pickerTrigger.getAttribute('aria-expanded') === 'true') {
          this.closePicker();
          this.pickerTrigger.focus();
        }
      });

      // initial label sync
      const selectedItem = this.pickerList.querySelector('.is-selected');
      if (selectedItem) this.pickerLabel.textContent = selectedItem.dataset.title;
    }

    openPicker() {
      this.pickerTrigger.setAttribute('aria-expanded', 'true');
      this.pickerModal.hidden = false;
      // Lock background scroll while modal is open
      document.body.style.overflow = 'hidden';
    }

    closePicker() {
      this.pickerTrigger.setAttribute('aria-expanded', 'false');
      this.pickerModal.hidden = true;
      document.body.style.overflow = '';
    }

    selectPickerItem(handle, title) {
      this.pickerList.querySelectorAll('.bundle-product-picker__item').forEach((el) => {
        const isMatch = el.dataset.handle === handle;
        el.classList.toggle('is-selected', isMatch);
        el.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
      this.pickerLabel.textContent = title;

      // sync hidden native select & dispatch change so existing logic runs
      this.rightProductSelect.value = handle;
      this.rightProductSelect.dispatchEvent(new Event('change', { bubbles: true }));

      this.closePicker();
    }

    async loadBundleProducts() {
      const handles = Array.from(this.rightProductSelect.options).map((o) => o.value);
      if (!handles.length) return;

      const queries = handles
        .map(
          (handle, i) => `
          p${i}: productByHandle(handle: "${handle}") {
            id
            title
            handle
            featuredImage { url altText }
            options { name }
            variants(first: 100) {
              nodes {
                id
                title
                availableForSale
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                selectedOptions { name value }
              }
            }
          }`
        )
        .join('\n');

      const query = `query bundleProducts { ${queries} }`;

      try {
        const res = await fetch(`https://${this.shopDomain}/api/2023-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query }),
        });
        const json = await res.json();
        if (!json.data) {
          console.error('[bundle-recomendation] Storefront API error', json.errors);
          return;
        }
        handles.forEach((handle, i) => {
          const data = json.data[`p${i}`];
          if (data) this.bundleProducts.set(handle, data);
        });

        const initialHandle = this.rightProductSelect.value || handles[0];
        this.renderRightProduct(initialHandle);
      } catch (err) {
        console.error('[bundle-recomendation] fetch failed', err);
      }
    }

    renderRightProduct(handle) {
      const data = this.bundleProducts.get(handle);
      if (!data) return;

      // Image
      if (data.featuredImage && data.featuredImage.url) {
        this.rightImg.src = this.resizeImage(data.featuredImage.url, 400);
        this.rightImg.alt = data.featuredImage.altText || data.title;
      }

      // Option name is intentionally NOT overwritten with the product's option name
      // to keep a single, locale-aware label across both slots (see size_label i18n key).

      // Variant select
      const variantSelect = this.rightVariantSelect;
      variantSelect.innerHTML = '';
      variantSelect.disabled = false;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = window.bundleRecoStrings && window.bundleRecoStrings.chooseSize
        ? window.bundleRecoStrings.chooseSize
        : 'Select size';
      variantSelect.appendChild(placeholder);

      data.variants.nodes.forEach((variant) => {
        const opt = document.createElement('option');
        const numericId = variant.id.split('/').pop();
        opt.value = variant.id;
        opt.dataset.numericId = numericId;
        opt.dataset.price = variant.price.amount;
        opt.dataset.compareAtPrice = variant.compareAtPrice ? variant.compareAtPrice.amount : 0;
        opt.dataset.available = variant.availableForSale;
        opt.dataset.optionValue = variant.title;
        opt.textContent = variant.availableForSale ? variant.title : `${variant.title} — ${this.tr('soldOut', 'Sold out')}`;
        if (!variant.availableForSale) opt.disabled = true;
        variantSelect.appendChild(opt);
      });

      this.rightSelected = null;
      this.updateRightPriceDisplay(data);
      this.updateRightOptionLabel();
      this.updateTotals();
    }

    updateRightPriceDisplay(data) {
      // Show first variant price as initial display
      const firstVariant = data.variants.nodes[0];
      if (!firstVariant) {
        this.rightPrice.innerHTML = '<span class="bundle-reco__price-placeholder">—</span>';
        return;
      }
      const price = parseFloat(firstVariant.price.amount) * 100;
      const compareAt = firstVariant.compareAtPrice ? parseFloat(firstVariant.compareAtPrice.amount) * 100 : 0;
      this.rightDefaultPrice = price;
      this.updateTotals();
      let html;
      if (compareAt > price) {
        // Match the layout produced by snippets/price.liquid (left card):
        // original price (line-through) FIRST, then sale price.
        html = `<div class="price price--on-sale">
                  <div class="price__sale">
                    <span class="price-item price-item--regular">${this.formatMoney(compareAt)}</span>
                    <span class="price-item price-item--sale price-item--last">${this.formatMoney(price)}</span>
                  </div>
                </div>`;
      } else {
        html = `<div class="price">
                  <div class="price__regular">
                    <span class="price-item price-item--regular">${this.formatMoney(price)}</span>
                  </div>
                </div>`;
      }
      this.rightPrice.innerHTML = html;
    }

    updateLeftOptionLabel() {
      const labelEl = this.leftCard.querySelector('[data-bundle-option-value]');
      if (!labelEl) return;
      labelEl.textContent = this.leftSelected ? this.leftSelected.optionValue : '—';
    }

    updateRightOptionLabel() {
      const labelEl = this.rightCard.querySelector('[data-bundle-option-value]');
      if (!labelEl) return;
      labelEl.textContent = this.rightSelected ? this.rightSelected.optionValue : '—';
    }

    toggleActiveCard() {
      this.leftCard.dataset.active = this.leftSelected ? 'true' : 'false';
      this.rightCard.dataset.active = this.rightSelected ? 'true' : 'false';
    }

    updateTotals() {
      // Always show the bundle price. Use the user's selected variants when present,
      // otherwise fall back to the default first-variant price for each side.
      // Bundle products share the same price across all variants, so the total is
      // accurate from the moment the page loads.
      const leftPrice = this.leftSelected ? this.leftSelected.price : this.leftDefaultPrice;
      const rightPrice = this.rightSelected ? this.rightSelected.price : this.rightDefaultPrice;

      // If we don't have a price for either side yet (right product still loading),
      // keep the placeholder.
      if (!leftPrice || !rightPrice) {
        this.totalFinal.textContent = '—';
        this.totalOriginal.hidden = true;
        this.totalSaved.hidden = true;
        return;
      }

      const percent = this.discountPercent;
      const subtotal = leftPrice + rightPrice;
      const discounted = subtotal * (1 - percent / 100);
      const saved = subtotal - discounted;

      this.totalFinal.textContent = this.formatMoney(discounted);

      if (percent > 0) {
        this.totalOriginal.textContent = this.formatMoney(subtotal);
        this.totalOriginal.hidden = false;
        const savedTpl = this.tr('youSaveHtml', 'You save {{ amount }}');
        this.totalSaved.innerHTML = savedTpl.replace('{{ amount }}', this.formatMoney(saved));
        this.totalSaved.hidden = false;
      } else {
        this.totalOriginal.hidden = true;
        this.totalSaved.hidden = true;
      }
    }

    async onAddToCart() {
      this.hideError();

      if (!this.leftSelected || !this.rightSelected) {
        this.showError(this.tr('selectBothError', 'Please select a size for both products.'));
        return;
      }
      if (!this.leftSelected.available || !this.rightSelected.available) {
        this.showError(this.tr('soldOutError', 'One of the selected variants is sold out.'));
        return;
      }

      this.addBtn.setAttribute('aria-disabled', 'true');
      this.addBtn.classList.add('loading');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      try {
        // Step 1: add both items
        const requestBody = {
          items: [
            { id: parseInt(this.leftSelected.id, 10), quantity: 1 },
            { id: parseInt(this.rightSelected.id, 10), quantity: 1 },
          ],
        };
        // eslint-disable-next-line no-console
        console.log('[bundle-recomendation] POST /cart/add.js body:', JSON.stringify(requestBody, null, 2));

        const addRes = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add.js'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(requestBody),
        });
        const addData = await addRes.json();
        // eslint-disable-next-line no-console
        console.log('[bundle-recomendation] /cart/add.js response:', addData);

        if (addData.status || addData.errors) {
          this.showError(addData.description || addData.message || this.tr('genericError', 'Could not add to cart.'));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'bundle-recomendation',
              errors: addData.errors || addData.description,
              message: addData.message,
            });
          }
          return;
        }

        // GTM add_to_cart_bundle push — disabled 2026-08: tracking migrated to
        // the official Google & YouTube app (web pixel).
        /*
        try {
          const bundleItems = Array.isArray(addData.items) ? addData.items : [];
          const shopCurrency =
            (window.Shopify && Shopify.currency && Shopify.currency.active) ||
            this.currencyCode ||
            'EUR';
          window.dataLayer = window.dataLayer || [];
          bundleItems.forEach((item) => {
            const variantId = item.id || item.variant_id;
            const quantity = item.quantity || 1;
            const priceCents = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
            const unitPrice = priceCents / 100;
            window.dataLayer.push({
              event: 'add_to_cart_bundle',
              product_id: variantId,
              product_name: item.product_title || item.title || '',
              product_price: unitPrice,
              product_quantity: quantity,
              currency: shopCurrency,
              value: unitPrice * quantity,
            });
          });
        } catch (e) {
          console.warn('add_to_cart_bundle push failed', e);
        }
        */

        // Step 2: fetch each section's HTML separately via `?section_id=<id>` —
        // this is the same endpoint cart.js uses internally (see cart.js#onCartUpdate)
        // and returns raw HTML reliably, unlike `/cart?sections=` which is JSON
        // and behaves inconsistently across Shopify backends.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-recomendation' });
        }

        if (cart && typeof cart.renderContents === 'function' && typeof cart.getSectionsToRender === 'function') {
          const baseCartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
          const sections = cart.getSectionsToRender();

          const sectionsHtml = await Promise.all(
            sections.map((s) =>
              fetch(`${baseCartUrl}?section_id=${s.id}`)
                .then((r) => r.text())
                .then((html) => [s.id, html])
            )
          );
          const sectionsMap = Object.fromEntries(sectionsHtml);

          // DEBUG: inspect each section's HTML and whether the inner selector is present
          // eslint-disable-next-line no-console
          console.group('[bundle-recomendation] cart sections fetched');
          sections.forEach((s) => {
            const html = sectionsMap[s.id] || '';
            const wantedSelector = s.selector || '.shopify-section';
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const inner = doc.querySelector(wantedSelector);
            const innerHtmlLen = inner ? inner.innerHTML.length : 0;
            const hasDrawerInner = !!doc.querySelector('.drawer__inner');
            const hasDrawerClose = !!doc.querySelector('.drawer__close');
            console.log(`section "${s.id}":`, {
              length: html.length,
              wantedSelector,
              innerFound: !!inner,
              innerHtmlLen,
              hasDrawerInner,
              hasDrawerClose,
              snippet: html.slice(0, 300),
            });
          });
          console.groupEnd();

          // Build the parsedState shape that cart-drawer / cart-notification expect
          const parsedState = {
            sections: sectionsMap,
            items: addData.items || [],
          };
          if (cart.tagName === 'CART-NOTIFICATION' && addData.items && addData.items[0]) {
            parsedState.key = addData.items[0].key;
          }

          // The <cart-drawer> element gets the `is-empty` class on initial render
          // when the cart was empty. After we add items it must be removed, otherwise
          // CSS rules like `cart-drawer.is-empty .drawer__header { display: none }`
          // hide the freshly rendered content. cart.renderContents() does NOT touch
          // the host element's classList, so we do it explicitly here.
          if (cart.tagName === 'CART-DRAWER') {
            cart.classList.remove('is-empty');
            const innerCartItems = cart.querySelector('cart-drawer-items');
            if (innerCartItems) innerCartItems.classList.remove('is-empty');
          }

          cart.renderContents(parsedState);
        }
      } catch (err) {
        console.error('[bundle-recomendation] add to cart failed', err);
        this.showError(this.tr('genericError', 'Could not add to cart.'));
      } finally {
        this.addBtn.removeAttribute('aria-disabled');
        this.addBtn.classList.remove('loading');
      }
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      if (this.errorTimer) clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => this.hideError(), 3000);
    }

    hideError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      if (this.errorTimer) {
        clearTimeout(this.errorTimer);
        this.errorTimer = null;
      }
    }

    formatMoney(cents) {
      // cents is integer (Shopify Liquid `money` filter friendly)
      const amount = cents / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currencyCode,
        }).format(amount);
      } catch (_) {
        return `${amount.toFixed(2)} ${this.currencyCode}`;
      }
    }

    resizeImage(url, width) {
      if (!url) return '';
      try {
        const u = new URL(url);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch (_) {
        return url;
      }
    }

    tr(key, fallback) {
      const dict = window.bundleRecoStrings || {};
      return dict[key] || fallback;
    }
  }

  customElements.define('bundle-recomendation', BundleRecomendation);
}
